import React from 'react'
import styled from 'styled-components'
import { Text, Span } from '../ui/Typography'
import { Flex } from '../ui/Layout'
import Button from '../ui/Button'
import Icon from '../ui/Icon'
import { openChat } from './ChatButton'
import ToggleRedGreen from '../ui/ToggleRedGreen'
import plans from '../Plans'
import { useAuth } from '../Auth'

const PriceCard = styled(Flex)`
  background-color: white;
  position: relative;
  flex-direction: column;
  padding-top: 32px;
  padding-bottom: 24px;
  padding-left: 16px;
  padding-right: 16px;
  border: ${(props) => (props.isMostPopular ? '2px solid black' : '1px solid #a0aec0')};
  border-radius: 16px;
  margin-right: ${(props) => (props.isLast ? '0px' : '16px')};
`

const PopularBadge = styled(Flex)`
  position: absolute;
  top: 10px;
  right: 10px;
  width: auto;
  background: linear-gradient(225deg, #bb65ff 0%, #4c6fff 100%);
  border-radius: 12px;
  padding-left: 10px;
  padding-right: 10px;
  padding-top: 3px;
  padding-bottom: 3px;
`

const PricingCards = ({ plansToShow, isAnnual, setIsAnnual, addPlan, showSpinner, canAlwaysSelectFree }) => {
  const { user } = useAuth()
  const isShopify = !!user?.shopifyShopUrl
  const currentPlan = user?.stripePlanId || user?.shopifyPlanId
  const hasFreeTrial = (() => {
    if (!currentPlan) return true
    if (currentPlan === 1 && !user.cancelledAt) return true
    return false
  })()

  return (
    <>
      {!isShopify ? (
        <Flex alignItems="center" mb={24}>
          <ToggleRedGreen
            setSelected={async (selected) => {
              setIsAnnual(selected)
            }}
            value1={false}
            value2={true}
            colorOveride="#000"
            selected={isAnnual}
          />
          <Text ml="8px" fontWeight="600">
            20% Annual Discount
          </Text>
        </Flex>
      ) : null}
      <Flex mb={24}>
        {plansToShow.map((plan, i) => {
          const planData = plans[plan]
          const isMostPopular = plan === 6
          const isCurrentPlan = (currentPlan === plan && user.isAnnual === isAnnual) || (currentPlan === 1 && plan === 1)
          const isCurrentPlanOppositeTimeFrame = currentPlan === plan && user.isAnnual !== isAnnual
          let buttonLabel = hasFreeTrial ? 'Start 14 day free trial' : 'Upgrade'
          if (!hasFreeTrial && currentPlan && currentPlan > plan) {
            buttonLabel = 'Downgrade'
          }
          if (plan === 1) {
            buttonLabel = 'Select Plan'
          }
          if (currentPlan && currentPlan > 1 && plan === 1) {
            buttonLabel = 'Cancel subscription'
          }
          if (isCurrentPlanOppositeTimeFrame) {
            buttonLabel = user.isAnnual ? 'Switch to monthly' : 'Switch to annual'
          }
          if (isCurrentPlan) {
            buttonLabel = 'Current plan'
          }
          return (
            <PriceCard key={plan} isMostPopular={isMostPopular} isLast={i === plansToShow.length - 1}>
              {isMostPopular && (
                <PopularBadge>
                  <Text color="white">Most popular</Text>
                </PopularBadge>
              )}
              <Flex flexDirection="column">
                <Text fontSize="20px" lineHeight="136%" fontWeight="600" mb="8px">
                  {planData.displayName}
                </Text>
                <Text height="40px" lineHeight="136%" light mb="8px">
                  {planData.subtitle}
                </Text>
              </Flex>

              <Flex flexDirection="column" mb="32px">
                <Text fontSize="28px" fontWeight="600" lineHeight="136%">
                  ${isAnnual ? (planData.annualPrice ? planData.annualPrice / 12 : 0) : planData.price}
                </Text>
                <Text fontSize="13px" lineHeight="136%" lightLight>
                  {plan === 1
                    ? 'Free'
                    : `$${isAnnual ? (planData.annualPrice ? planData.annualPrice / 12 : 0) : planData.price} per month ${
                        isAnnual ? '/ billed annually' : ''
                      }`}
                </Text>
              </Flex>

              <Button
                variant="white"
                mb="32px"
                isDisabled={isCurrentPlan && !(canAlwaysSelectFree && plan === 1)}
                label={buttonLabel}
                borderColor={isMostPopular ? '' : 'black'}
                color={isMostPopular ? 'white' : 'black'}
                borderWidth={isMostPopular ? 0 : 1}
                background={isMostPopular ? 'linear-gradient(225deg, #BB65FF 0%, #4C6FFF 100%);' : 'white'}
                height="46px"
                onClick={() => addPlan(plan)}
                isLoading={showSpinner}
              />

              <Flex flexDirection="column">
                <Flex alignItems="center">
                  <Icon icon="check-black" width="11px" height="8px" />
                  <Text lightLight ml="8px" fontSize="13px" lineHeight="16px">
                    {planData.storageLimitsDisplay}
                  </Text>
                </Flex>
                {/* <Flex alignItems="center" mt="8px">
                  <Icon icon="check-black" width="11px" height="8px" />
                  <Text lightLight ml="8px" fontSize="13px" lineHeight="16px">
                    {planData.streamingLimitsDisplay}
                  </Text>
                </Flex> */}
                <Flex height="1px" backgroundColor="#EDF2F7" my="16px" />
                {planData.subfeaturesTitle && (
                  <Text fontSize="14px" lineHeight="16px" mb="16px" fontWeight="600">
                    {planData.subfeaturesTitle}
                  </Text>
                )}
                {planData.subfeatures?.map((feature) => (
                  <Flex alignItems="center" mb="8px" key={feature}>
                    <Icon icon="check-black" width="11px" height="8px" />
                    <Text lightLight ml="8px" fontSize="13px" lineHeight="16px">
                      {feature}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            </PriceCard>
          )
        })}
      </Flex>

      <Flex alignItems="center">
        <Text fontSize="13px" lineHeight="16px" light textAlign="center" width="100%">
          Monthly streaming limits subject to fair use policy.{' '}
          <Span cursor="pointer" underline fontWeight="700" onClick={openChat}>
            Contact Sales
          </Span>
        </Text>
      </Flex>
    </>
  )
}

export default PricingCards
